// NEW ENHANCED VERSION - Site Logo & Hero Background Settings
import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import ImageUpload from '../../components/admin/ImageUpload';
import VideoUpload from '../../components/admin/VideoUpload';

const SiteSettingsManagement = () => {
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    site_logo: '',
    home_settings: {
      hero_background_type: 'image', // image, youtube, video
      hero_background_image: '',
      hero_background_youtube: '',
      hero_background_video: '',
      hero_title_ar: '',
      hero_subtitle_ar: '',
    },
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/settings/`);
      const data = await response.json();
      setFormData(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/settings/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        alert('تم حفظ الإعدادات بنجاح ✅');
      }
    } catch (error) {
      alert('حدث خطأ في الحفظ ❌');
    }
  };

  const updateHomeSettings = (key, value) => {
    setFormData({
      ...formData,
      home_settings: {
        ...formData.home_settings,
        [key]: value
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F388E]"></div>
          <p className="mt-4 text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-[#0F388E]">إعدادات الموقع - الشعار والخلفية</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Logo Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="bg-[#0F388E] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">1</span>
              شعار الجامعة
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ImageUpload
              label="شعار الجامعة (Logo)"
              value={formData.site_logo || ''}
              onChange={(v) => setFormData({...formData, site_logo: v})}
            />
            <p className="text-sm text-gray-500 mt-2">
              💡 سيظهر الشعار في شريط التنقل (Navbar) في جميع صفحات الموقع
            </p>
          </CardContent>
        </Card>

        {/* Hero Background Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="bg-[#0F388E] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">2</span>
              خلفية الصفحة الرئيسية (Hero Section)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Background Type Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                نوع الخلفية
              </label>
              <div className="grid grid-cols-3 gap-4">
                <button
                  type="button"
                  onClick={() => updateHomeSettings('hero_background_type', 'image')}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    formData.home_settings?.hero_background_type === 'image'
                      ? 'border-[#0F388E] bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-3xl mb-2">🖼️</div>
                    <div className="font-medium">صورة</div>
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => updateHomeSettings('hero_background_type', 'youtube')}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    formData.home_settings?.hero_background_type === 'youtube'
                      ? 'border-[#0F388E] bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-3xl mb-2">📺</div>
                    <div className="font-medium">فيديو YouTube</div>
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => updateHomeSettings('hero_background_type', 'video')}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    formData.home_settings?.hero_background_type === 'video'
                      ? 'border-[#0F388E] bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-3xl mb-2">🎬</div>
                    <div className="font-medium">فيديو مرفوع</div>
                  </div>
                </button>
              </div>
            </div>

            {/* Image Background */}
            {formData.home_settings?.hero_background_type === 'image' && (
              <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                <h3 className="font-bold text-[#0F388E] mb-4">صورة الخلفية</h3>
                <ImageUpload
                  label="اختر صورة الخلفية"
                  value={formData.home_settings?.hero_background_image || ''}
                  onChange={(v) => updateHomeSettings('hero_background_image', v)}
                />
                <p className="text-sm text-gray-600 mt-2">
                  💡 يُفضل استخدام صورة عالية الدقة (1920x1080) أو أكبر
                </p>
              </div>
            )}

            {/* YouTube Background */}
            {formData.home_settings?.hero_background_type === 'youtube' && (
              <div className="bg-red-50 p-6 rounded-lg border border-red-200">
                <h3 className="font-bold text-[#0F388E] mb-4">رابط فيديو YouTube</h3>
                <input
                  type="url"
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={formData.home_settings?.hero_background_youtube || ''}
                  onChange={(e) => updateHomeSettings('hero_background_youtube', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
                <p className="text-sm text-gray-600 mt-2">
                  💡 الفيديو سيعمل تلقائياً ومتكرر (autoplay + loop) في الخلفية
                </p>
              </div>
            )}

            {/* Uploaded Video Background */}
            {formData.home_settings?.hero_background_type === 'video' && (
              <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
                <h3 className="font-bold text-[#0F388E] mb-4">رفع فيديو من جهازك</h3>
                <VideoUpload
                  label="اختر ملف فيديو (MP4, WebM, MOV - حتى 100MB)"
                  value={formData.home_settings?.hero_background_video || ''}
                  onChange={(v) => updateHomeSettings('hero_background_video', v)}
                />
                <p className="text-sm text-gray-600 mt-2">
                  💡 الفيديو سيعمل تلقائياً ومتكرر (autoplay + loop) في الخلفية
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Founder Message */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">كلمة مؤسس الجامعة</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-100 space-y-4">
              <h3 className="font-bold text-[#0F388E] text-lg">معلومات المؤسس</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    اسم المؤسس بالعربي
                  </label>
                  <input
                    type="text"
                    value={formData.founder_name_ar || ''}
                    onChange={(e) => setFormData({...formData, founder_name_ar: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                    placeholder="أ.د/ خالد احمد الضرعي"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Founder Name in English
                  </label>
                  <input
                    type="text"
                    value={formData.founder_name_en || ''}
                    onChange={(e) => setFormData({...formData, founder_name_en: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                    placeholder="Prof. Dr. Khaled Ahmed Al-Darii"
                  />
                </div>
              </div>
              
              <ImageUpload
                label="صورة المؤسس"
                value={formData.founder_image || ''}
                onChange={(v) => setFormData({...formData, founder_image: v})}
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  كلمة المؤسس بالعربي
                </label>
                <textarea
                  value={formData.founder_message_ar || ''}
                  onChange={(e) => setFormData({...formData, founder_message_ar: e.target.value})}
                  rows={8}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                  placeholder="اكتب كلمة مؤسس الجامعة..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Founder Message in English
                </label>
                <textarea
                  value={formData.founder_message_en || ''}
                  onChange={(e) => setFormData({...formData, founder_message_en: e.target.value})}
                  rows={8}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                  placeholder="Write founder's message..."
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* President Message */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">كلمة رئيس الجامعة</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg border border-green-100 space-y-4">
              <h3 className="font-bold text-[#0F388E] text-lg">معلومات رئيس الجامعة</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    اسم رئيس الجامعة بالعربي
                  </label>
                  <input
                    type="text"
                    value={formData.president_name_ar || ''}
                    onChange={(e) => setFormData({...formData, president_name_ar: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                    placeholder="أ.د/ عبد السلام دلاق"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    President Name in English
                  </label>
                  <input
                    type="text"
                    value={formData.president_name_en || ''}
                    onChange={(e) => setFormData({...formData, president_name_en: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                    placeholder="Prof. Dr. Abdul Salam Dallaq"
                  />
                </div>
              </div>
              
              <ImageUpload
                label="صورة رئيس الجامعة"
                value={formData.president_image || ''}
                onChange={(v) => setFormData({...formData, president_image: v})}
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  كلمة رئيس الجامعة بالعربي
                </label>
                <textarea
                  value={formData.president_message_ar || ''}
                  onChange={(e) => setFormData({...formData, president_message_ar: e.target.value})}
                  rows={8}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                  placeholder="اكتب كلمة رئيس الجامعة..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  President Message in English
                </label>
                <textarea
                  value={formData.president_message_en || ''}
                  onChange={(e) => setFormData({...formData, president_message_en: e.target.value})}
                  rows={8}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                  placeholder="Write president's message..."
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button type="submit" className="bg-[#0F388E] px-8">
            <Save className="ml-2 w-5 h-5" />
            حفظ جميع الإعدادات
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SiteSettingsManagement;
